import { all, takeEvery, put, fork, call, select } from 'redux-saga/effects';
import { message } from 'antd';
import actions from './actions';
import { ApiClient } from '../../../api';
import { makeSelectPageToken } from '../../socialPage/selector';
import config from '../../../settings';

// Create selectors once for optimal performance
const selectPageToken = makeSelectPageToken();

function* getApiKeysSaga() {
  yield takeEvery(actions.GET_API_KEYS, function*({ options }) {
    try {
      // Use pageToken for Page Admin operations (API keys management)
      const pageToken = yield select(selectPageToken);
      const apiClient = new ApiClient(config.apiUrl, pageToken);
      const response = yield call(() => apiClient.getApiKeys(options));
      if (response.success) {
        yield put(actions.getApiKeysSuccess(response.data, response.total));
      } else {
        yield put(actions.getApiKeysError(response.message || 'Failed to fetch API keys'));
        message.error('Failed to fetch API keys');
      }
    } catch (error) {
      yield put(actions.getApiKeysError(error.message));
      message.error(`Error fetching API keys: ${error.message}`);
    }
  });
}

function* createApiKeySaga() {
  yield takeEvery(actions.CREATE_API_KEY, function*({ keyData }) {
    try {
      // Use pageToken for Page Admin operations (API keys management)
      const pageToken = yield select(selectPageToken);
      const apiClient = new ApiClient(config.apiUrl, pageToken);
      const response = yield call(() => apiClient.createApiKey(keyData));
      if (response.success) {
        yield put(actions.createApiKeySuccess(response.data));
        // Don't show message here, let the success modal handle it
      } else {
        yield put(actions.createApiKeyError(response.message || 'Failed to create API key'));
        message.error('Failed to create API key');
      }
    } catch (error) {
      yield put(actions.createApiKeyError(error.message));
      message.error(`Error creating API key: ${error.message}`);
    }
  });
}

function* updateApiKeySaga() {
  yield takeEvery(actions.UPDATE_API_KEY, function*({ keyId, keyData }) {
    try {
      // Use pageToken for Page Admin operations (API keys management)
      const pageToken = yield select(selectPageToken);
      const apiClient = new ApiClient(config.apiUrl, pageToken);
      const response = yield call(() => apiClient.updateApiKey(keyId, keyData));
      if (response.success) {
        yield put(actions.updateApiKeySuccess(response.data));
        message.success('API key updated successfully');
      } else {
        yield put(actions.updateApiKeyError(response.message || 'Failed to update API key'));
        message.error('Failed to update API key');
      }
    } catch (error) {
      yield put(actions.updateApiKeyError(error.message));
      message.error(`Error updating API key: ${error.message}`);
    }
  });
}

function* deleteApiKeySaga() {
  yield takeEvery(actions.DELETE_API_KEY, function*({ keyId }) {
    try {
      // Use pageToken for Page Admin operations (API keys management)
      const pageToken = yield select(selectPageToken);
      const apiClient = new ApiClient(config.apiUrl, pageToken);
      const response = yield call(() => apiClient.deleteApiKey(keyId));
      if (response.success) {
        yield put(actions.deleteApiKeySuccess(keyId));
        message.success('API key deleted successfully');
      } else {
        yield put(actions.deleteApiKeyError(response.message || 'Failed to delete API key'));
        message.error('Failed to delete API key');
      }
    } catch (error) {
      yield put(actions.deleteApiKeyError(error.message));
      message.error(`Error deleting API key: ${error.message}`);
    }
  });
}

function* regenerateApiKeySaga() {
  yield takeEvery(actions.REGENERATE_API_KEY, function*({ keyId }) {
    try {
      // Use pageToken for Page Admin operations (API keys management)
      const pageToken = yield select(selectPageToken);
      const apiClient = new ApiClient(config.apiUrl, pageToken);
      const response = yield call(() => apiClient.regenerateApiKey(keyId));
      if (response.success) {
        yield put(actions.regenerateApiKeySuccess(response.data));
        // Don't show message here, let the success modal handle it
      } else {
        yield put(actions.regenerateApiKeyError(response.message || 'Failed to regenerate API key'));
        message.error('Failed to regenerate API key');
      }
    } catch (error) {
      yield put(actions.regenerateApiKeyError(error.message));
      message.error(`Error regenerating API key: ${error.message}`);
    }
  });
}

export default function* apiKeysSaga() {
  yield all([
    fork(getApiKeysSaga),
    fork(createApiKeySaga),
    fork(updateApiKeySaga),
    fork(deleteApiKeySaga),
    fork(regenerateApiKeySaga),
  ]);
}
